import { EMPTY_CELL } from '@/shared/constants'

import { fixed, hasFigure } from './numberFormat'

/** Negative as drawn, so `-0.001` is `$ 0.00` and not `-$ 0.00`. */
const isNegative = (amount: number, digits = 2) =>
  amount < 0 &&
  fixed(digits).format(Math.abs(amount)) !== fixed(digits).format(0)

/**
 * A number drawn as money: `12.5` → `$ 12.50`, `-1234` → `-$ 1,234.00`. Always two decimal
 * places and a thousands separator, the minus before the mark; `null`, `undefined` and
 * `NaN` are `—`, never `$ 0.00`.
 *
 * `sign` is the mark, **not** a currency code and not a precision — the two like-named helpers
 * outside this library take one of those, so check which one you are calling.
 */
export function formatMoney(
  amount: number | null | undefined,
  sign = '$'
): string {
  if (!hasFigure(amount)) return EMPTY_CELL

  const body = `${sign} ${fixed(2).format(Math.abs(amount))}`

  return isNegative(amount) ? `-${body}` : body
}

/**
 * `formatMoney` rounded to whole units, with no thousands separator: `44848.49` → `$ 44848`,
 * `-0.4` → `$ 0`. For a headline figure where cents are noise; a column that is summed by
 * eye keeps `formatMoney`.
 */
export function formatWholeMoney(
  amount: number | null | undefined,
  sign = '$'
): string {
  if (!hasFigure(amount)) return EMPTY_CELL

  const body = `${sign} ${Math.round(Math.abs(amount))}`

  return isNegative(amount, 0) ? `-${body}` : body
}

/**
 * A number drawn with a currency code after it: `50` and `'EUR'` → `50.00 EUR`. The same digits
 * as `formatMoney`, for a sum whose currency arrives as a code (`USD`, `EUR`, or several joined
 * with `/`) rather than as a mark; `null`, `undefined` and `NaN` are `—`.
 */
export function formatAmount(
  amount: number | null | undefined,
  currency: string
): string {
  if (!hasFigure(amount)) return EMPTY_CELL

  return `${isNegative(amount) ? '-' : ''}${fixed(2).format(Math.abs(amount))} ${currency}`.trim()
}
